/**
 * RELIEF — the shared shading capability.
 *
 * OWNER: piece:shared-shading. Nine other pieces code against this file. Treat every
 * exported name below as a contract: add to it, do not change it under someone.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 *
 * Across two waves six independent critics reviewing six different pieces converged on
 * one systemic problem stated six ways: cover patches that "read as flat khaki decals
 * pasted onto the ground"; terrain structure "painted as flat albedo marks instead of
 * form catching light"; ground-cover mats holding "a near-constant value across a 1-4 m
 * span while the terrain under it carries the light gradient"; rider value breaks that
 * are "a hard-edged colour patch painted onto a smooth primitive instead of a form
 * edge"; an ambient AO ring that is "effectively inert, so objects sit in uniformly lit
 * space"; dust puffs with "no sunward bright side and no shadowed underside".
 *
 * One problem: **everything is PAINTED rather than SHAPED, and nothing modulates the
 * scene's lighting.** Under an art direction whose first principle is "form and light
 * carry the image, not texture microdetail" (docs/BAR.md), that is the defining failure.
 * It is also why a piece that only owns albedo can do six competent rounds and stay at
 * 5-7: albedo is the wrong axis.
 *
 * This file owns the right axis. It contains no art. It contains the five operations
 * that turn a procedural scalar into something the sun can find:
 *
 *   1. NORMAL PERTURBATION  — any scalar field becomes surface form.
 *   2. COVER BLENDING       — a cover layer modulates the ground's light instead of
 *                             replacing it, with a broken margin and a contact rim.
 *   3. CONTACT OCCLUSION    — cheap analytic darkening where a thing meets the ground.
 *   4. LIT SPRITES          — a camera-facing quad with a real sunward face and a
 *                             genuinely darker underside that survives the sprite growing.
 *   5. ONE LOD POLICY       — so all of the above fades the same way at every distance
 *                             and the world does not look like several games at once.
 *
 * No texture files. Everything is hash-driven and deterministic — no `Math.random`, no
 * `sin()`-based hashes (those differ between drivers and band on mobile GPUs).
 *
 * ---------------------------------------------------------------------------
 * HOW TO USE IT — THE THIRTY SECOND VERSION
 *
 * Both material paths in this repo are supported and they share the same GLSL.
 *
 *   A) MeshStandardMaterial + onBeforeCompile (terrain, vegetation, bike, trail):
 *
 *        import { patchStandardMaterial } from './shading/relief';
 *
 *        const mat = new THREE.MeshStandardMaterial({ ... });
 *        patchStandardMaterial(mat, { cacheKey: 'my-piece-v1', shade: MY_GLSL });
 *
 *      Inside `shade` you get `vRlfWPos`, `vRlfWNrm`, `vRlfDist` and the whole `rlf*`
 *      library, and you write three globals: `gRlfNormalW` (world-space shading normal),
 *      `gRlfRough` (roughness) and `gRlfOcc` (occlusion multiplier). The patch wires all
 *      three into three's own lighting, so you keep shadow receiving, the sky probe, the
 *      terrain self-shadow that sky.ts injects globally, fog/aerial perspective and ACES
 *      for free. See `exampleGroundMaterial()` at the bottom of this file — it compiles.
 *
 *   B) Raw ShaderMaterial (particles, tracks, anything hand-lit):
 *
 *        import { reliefGLSL, reliefUniforms } from './shading/relief';
 *
 *        new THREE.ShaderMaterial({
 *          vertexShader:   reliefGLSL('vertex')   + MY_VERT,
 *          fragmentShader: reliefGLSL('fragment') + MY_FRAG,
 *          uniforms: { ...reliefUniforms(), ...myUniforms },
 *        });
 *
 *      `THREE.RawShaderMaterial` is NOT supported — the library needs three's standard
 *      uniform prefix (`viewMatrix`, `cameraPosition`). Use `ShaderMaterial`.
 *
 *   C) You already have a big onBeforeCompile of your own (terrain-shading does):
 *
 *        import { reliefGLSL, mergeReliefUniforms, RELIEF_VARYINGS, RELIEF_VERTEX_BODY,
 *                 RELIEF_STD_GLOBALS, RELIEF_STD_AO_PATCH } from './shading/relief';
 *
 *      `patchStandardMaterial` would clobber your patch. Take the pieces instead:
 *      `reliefGLSL('fragment')` + `RELIEF_STD_GLOBALS` into `<common>`,
 *      `RELIEF_VERTEX_BODY` after `<project_vertex>`, `RELIEF_STD_AO_PATCH` over
 *      `<aomap_fragment>`, and one `mergeReliefUniforms(shader)` call. The GLSL is
 *      include-guarded, so it is safe next to anything you already inject.
 *
 * And once, from any one of the nine consumers (it is idempotent, so all nine may call
 * it): `installReliefSystem()`. That keeps the shared light block in step with the sky.
 * If nobody ever calls it the library still works — it falls back to a fixed midday sun
 * and midday sky, which looks fine and is wrong at dawn.
 *
 * ---------------------------------------------------------------------------
 * THE FIVE RULES THIS LIBRARY EXISTS TO ENFORCE
 *
 * 1. **A pattern you can see must be a pattern the sun can find.** If you compute a
 *    scalar and put it in albedo, put it through `rlfBumpH` / `rlfBumpSlope` as well.
 *    Half the amplitude in the normal is worth more than twice the amplitude in colour.
 *
 * 2. **Cover modulates, it never replaces.** A duff patch, a moss mat, a tread, a
 *    lichen face: blend the *albedo* and the *roughness*, perturb the *normal*, and let
 *    the light term underneath come through untouched. `rlfCoverApply` does exactly
 *    that and nothing else. If your cover ends up with a constant value across a 3 m
 *    span you have replaced the lighting somewhere.
 *
 * 3. **No level sets.** A boundary produced by `smoothstep(e - fwidth, e + fwidth, x)`
 *    is a one-pixel step: it has no thickness, no feathering, no contact. Every margin
 *    in this game goes through `rlfCoverBlend`, which floors the feather at an *art*
 *    width, interrupts it with noise, and puts an occlusion band just outside it.
 *
 * 4. **Everything that touches the ground gets a contact term.** `rlfContact`,
 *    `rlfContactAt`, `rlfCrease`. Under this art direction an object floating on the
 *    surface is the single most damaging artefact (docs/BAR.md, Post-processing).
 *
 * 5. **One fade.** Use `rlfLodAt` + `rlfReliefAmp` / `rlfAlbedoAmp`. Do not invent your
 *    own distance ramp; if yours differs from everyone else's the mountain will visibly
 *    change materials as you ride down it.
 *
 * ---------------------------------------------------------------------------
 * COST
 *
 * Everything here is ALU and derivatives. No texture fetch, no dependent read, no loop
 * longer than six iterations, no branch that is not uniform across a quad in practice.
 * Per-fragment estimates (counting a madd as one, a transcendental as four):
 *
 *   rlfEnv()                 ~8      pure uniform reads, hoisted by every compiler
 *   rlfLodAt()               ~22     2 vec3 derivative pairs + 2 length + smoothstep
 *   rlfNoiseD()              ~34     4 hashes (~6 each) + quintic + 2 lerps
 *   rlfFbmD(p, 3)            ~110    3 x rlfNoiseD + chain rule
 *   rlfReliefField(3 oct)    ~114
 *   rlfBumpH()               ~30     4 derivatives, 2 cross, 2 dot, 1 normalize, 1 length
 *   rlfBumpSlope()           ~14
 *   rlfCoverField()          ~180    2 fBm at different scales
 *   rlfCoverBlend()          ~130    1 fBm(3) + fwidth + 2 smoothstep + 1 exp2
 *   rlfCoverApply()          ~36     mix + mix + rlfBumpH
 *   rlfContact/At/Crease     ~10 each
 *   rlfSpriteNormal()        ~26     1 sqrt, 2 sin, 1 normalize
 *   rlfSpriteShade()         ~46     2 exp2, 1 mix chain, no derivatives
 *
 * A terrain fragment using the full ground path (lod + one relief field + one cover
 * field + cover blend + apply) is ~590 ALU. At 1080p with 1.6x overdraw that is roughly
 * 2.0 GFLOP/frame; a 2019-era integrated GPU sustains ~400-700 GFLOP/s, so **~0.3 ms**
 * of the 16.6 ms budget. A dust fragment is ~90 ALU and dust covers a few per cent of
 * the screen: **under 0.05 ms**. Those are estimates from instruction counts, not
 * measurements — nothing consumes this file yet. Price your own adoption by A/B-ing two
 * capture runs (docs/AGENTS.md, "Reading the frame numbers honestly": `medianFrameMs`
 * delta between runs, never `p95` and never `medianSystemMs`, which cannot see fragment
 * cost at all).
 *
 * If you are fragment-bound, the two knobs that actually pay are: drop `rlfCoverField`
 * to a single fBm octave count, and gate `rlfCoverBlend` behind `L.farFade < 0.98`.
 *
 * ---------------------------------------------------------------------------
 * GLSL API — the full surface, in the order it appears below
 *
 * ENVIRONMENT (stage-agnostic)
 *   struct RlfEnv { vec3 sunW; float sunVis; vec3 key, sky, gnd; float motion, time, scale; }
 *   RlfEnv rlfEnv()                                resolved light block, with fallbacks
 *   vec3   rlfToView(vec3 worldDir)                world direction -> view direction
 *
 * FIELDS (stage-agnostic, deterministic)
 *   float rlfHash21(vec2)  vec2 rlfHash22(vec2)  float rlfHash31(vec3)
 *   vec3  rlfNoiseD(vec2 p)                        (value 0..1, dv/dx, dv/dy)
 *   vec3  rlfFbmD(vec2 p, int oct, float gain)     (value 0..1, gradient)
 *   float rlfFbm(vec2 p, int oct)
 *   vec3  rlfReliefField(vec2 uv, float freq, float reliefM, int oct)
 *                                                  (height m, dh/du, dh/dv) — no unit trap
 *
 * FRAME (stage-agnostic)
 *   vec2  rlfPlaneUV(vec3 wp, vec3 N)              detail-plane UV, metres
 *   void  rlfGroundFrame(vec3 N, out vec3 axU, out vec3 axV)   the matching frame
 *
 * NORMAL PERTURBATION
 *   vec3 rlfBumpSlope(vec3 N, vec3 axU, vec3 axV, vec2 slope, float amp)   analytic
 *   vec3 rlfBumpXZ(vec3 N, vec2 slopeXZ, float amp)                        analytic, ground
 *   vec3 rlfMatNormal(vec3 groundN, vec3 ownN, float ownWeight)            cover geometry
 *   vec3 rlfBumpH(vec3 N, vec3 wp, float h, float amp)          FRAGMENT ONLY, any scalar
 *   vec3 rlfFieldNormal(vec3 N, vec3 wp, float f01, float reliefM, float amp)  FRAGMENT ONLY
 *
 * CONTACT / EDGE OCCLUSION (stage-agnostic)
 *   float rlfContact(float r, float y, float R, float strength)
 *   float rlfContactAt(vec3 wp, vec3 centre, float R, float strength)
 *   float rlfCrease(float d, float wallH, float strength)
 *
 * SPRITES (stage-agnostic)
 *   vec2 rlfSpriteUp(float spin)                   world up in the quad's own uv frame
 *   vec3 rlfSpriteNormal(vec2 uv, float d, float bulge, float seed)
 *   struct RlfSpriteParams { float wrap, thickness, ext, trans, ao; }
 *   RlfSpriteParams rlfDustParams(float radiusM)
 *   RlfSpriteParams rlfFoliageParams(float thicknessM)
 *   vec3 rlfSpriteShade(vec3 nV, vec2 uv, vec2 upUV, float d, RlfSpriteParams P, RlfEnv E)
 *
 * LOD (FRAGMENT ONLY unless noted)
 *   struct RlfLod { float foot, aniso, graze, dist, farFade; }
 *   RlfLod rlfLodAt(vec3 wp)
 *   RlfLod rlfLodManual(float dist, float metresPerPixel)      stage-agnostic
 *   float  rlfBandLod(RlfLod L, float freq)        stage-agnostic — Nyquist fade
 *   float  rlfAlbedoAmp(RlfLod L, float freq)      stage-agnostic
 *   float  rlfReliefAmp(RlfLod L, float freq)      stage-agnostic — THE multiplier
 *   float  rlfFarFade(float dist)                  stage-agnostic
 *
 * COVER (FRAGMENT ONLY)
 *   struct RlfCoverParams { float level, margin, breakFreq, breakAmp, depth, occ, grain; }
 *   RlfCoverParams rlfCoverDefaults()
 *   float rlfCoverField(vec2 uv, float patchM, float seed)
 *   struct RlfCover { float w, h, occ, edge; }
 *   RlfCover rlfCoverBlend(float field, vec2 uv, RlfLod L, RlfCoverParams P)
 *   void rlfCoverApply(inout vec3 albedo, inout float rough, inout vec3 N,
 *                      vec3 wp, RlfCover C, vec3 coverAlbedo, float coverRough, float amp)
 *
 * ---------------------------------------------------------------------------
 * TYPESCRIPT API — the full surface
 *
 * GLSL DELIVERY
 *   reliefGLSL(stage: 'vertex' | 'fragment', withVaryings = true): string
 *   RELIEF_COMMON_GLSL      stage-agnostic half, include-guarded
 *   RELIEF_FRAGMENT_GLSL    fragment-only half, include-guarded, needs the above first
 *   RELIEF_VARYINGS         vRlfWPos / vRlfWNrm / vRlfDist declarations
 *   RELIEF_VERTEX_BODY      fills them; place after #include <project_vertex>
 *   RELIEF_STD_GLOBALS      gRlfNormalW / gRlfRough / gRlfOcc declarations
 *   RELIEF_STD_AO_PATCH     drop-in replacement for #include <aomap_fragment>
 *
 * WIRING
 *   reliefUniforms(): Record<string, THREE.IUniform>       shared by reference
 *   mergeReliefUniforms(shader): void                      for a hand-rolled patch
 *   patchStandardMaterial(material, ReliefStandardOptions): void
 *   installReliefSystem(): void                            idempotent; call it once
 *   updateRelief(camera, timeSeconds): void                idempotent within a frame
 *   setReliefSun(x, y, z): void                            override, for captures
 *
 * TUNING (mutable)
 *   ReliefTuning  { masterScale, motionCut, motionFullSpeed, keyScale, ambientScale }
 *   ReliefLod     { nyqBias, nyqRate, anisoMax, grazeRate, grazeMin,
 *                   farStart, farEnd, farFloorRelief, farFloorAlbedo }   (frozen; the
 *                 GLSL is generated from these at module load, so changing them at
 *                 runtime does nothing — change them here and recompile.)
 *
 * CPU MIRROR OF THE LOD POLICY — use these for tile dissolves, imposter switches and
 * particle sizing so the CPU and the shader fade on the same numbers
 *   reliefFarFade(dist): number
 *   reliefNyquist(freq, footprint): number
 *   reliefFootprint(dist, fovYDegrees, pixelHeight): number
 *   reliefAmp(dist, freq, footprint): number
 *
 * REFERENCE IMPLEMENTATIONS (compiled, nothing imports them — copy, do not call)
 *   exampleGroundMaterial(): THREE.MeshStandardMaterial
 *   exampleDustSpriteMaterial(): THREE.ShaderMaterial
 *
 * ---------------------------------------------------------------------------
 * WORKED EXAMPLE A — MeshStandardMaterial via onBeforeCompile
 *
 * Ground with duff cover that keeps the hillside's light gradient across its span. This
 * is `exampleGroundMaterial()` below, reproduced here so the contract is readable
 * without scrolling:
 *
 *   const SHADE = `
 *   {
 *     vec3 wp = vRlfWPos;
 *     vec3 N  = normalize( vRlfWNrm );
 *     RlfLod L = rlfLodAt( wp );
 *     vec2 uv = rlfPlaneUV( wp, N );          // ground plane, or the wall plane on cliffs
 *     vec3 axU, axV;
 *     rlfGroundFrame( N, axU, axV );          // the frame that matches that UV
 *
 *     // 1. BARE GROUND GETS FORM. 8 cm of relief at 0.7 cycles/m — clod scale.
 *     //    rlfReliefField hands back the slope already in metres per metre, so there is
 *     //    no frequency term to forget.
 *     vec3 R = rlfReliefField( uv, 0.7, 0.08, 3 );
 *     N = rlfBumpSlope( N, axU, axV, R.yz, rlfReliefAmp( L, 0.7 ) );
 *
 *     // 2. COVER. Note what is NOT here: no second light, no flat colour, no
 *     //    smoothstep( e - fwidth, e + fwidth, x ). The cover blends albedo and
 *     //    roughness, adds its own thickness to the normal, and hands back an
 *     //    occlusion multiplier for the rim.
 *     float f = rlfCoverField( uv, 6.0, 11.3 );
 *     RlfCoverParams P = rlfCoverDefaults();
 *     P.depth = 0.05;                          // 5 cm of duff
 *     RlfCover C = rlfCoverBlend( f, uv, L, P );
 *
 *     vec3  albedo = diffuseColor.rgb;
 *     float rough  = roughness;
 *     rlfCoverApply( albedo, rough, N, wp, C,
 *                    vec3( 0.107, 0.086, 0.055 ),   // duff, linear
 *                    0.97,
 *                    rlfReliefAmp( L, 1.0 ) );
 *
 *     // 3. HAND IT TO THREE. These three globals are all the patch reads.
 *     gRlfNormalW = N;
 *     gRlfRough   = rough;
 *     gRlfOcc     = C.occ;
 *     diffuseColor.rgb = albedo;
 *   }`;
 *
 *   patchStandardMaterial( mat, { cacheKey: 'grip-ground-example-v1', shade: SHADE } );
 *
 * The cover holds the terrain's light gradient because `C.w` never touches the light
 * term: three still computes NdotL, the shadow map, sky.ts's analytic ridge shadow and
 * the SH probe exactly once, from the normal we perturbed. A 4 m mat spanning a lit
 * shoulder and a shaded hollow therefore spans them too.
 *
 * ---------------------------------------------------------------------------
 * WORKED EXAMPLE B — raw ShaderMaterial (a lit dust sprite)
 *
 * This is `exampleDustSpriteMaterial()` below. The vertex shader expands a camera-facing
 * quad and passes the one extra varying the sprite path needs — world up expressed in
 * the quad's own uv frame:
 *
 *   VERTEX:
 *     vec4 mv = modelViewMatrix * vec4( iPos, 1.0 );
 *     float c = cos( iShape.y ), s = sin( iShape.y );
 *     vec2 q = vec2( position.x * c - position.y * s, position.x * s + position.y * c );
 *     mv.xy += q * iShape.x;
 *     gl_Position = projectionMatrix * mv;
 *     vUv     = position.xy;
 *     vUpUV   = rlfSpriteUp( iShape.y );   // <- the whole trick
 *     vRadius = iShape.x;
 *
 *   FRAGMENT:
 *     float d = length( vUv );
 *     if ( d > 1.0 ) discard;
 *     vec3 nV = rlfSpriteNormal( vUv, d, 0.38, vSeed );
 *     RlfSpriteParams P = rlfDustParams( vRadius );
 *     vec3 lit = rlfSpriteShade( nV, vUv, vUpUV, d, P, rlfEnv() );
 *     gl_FragColor = vec4( vCol.rgb * lit, vCol.a * smoothstep( 1.0, 0.55, d ) );
 *
 * `vUpUV` is why the underside stays dark. A camera-facing quad's own normal points at
 * the camera in the middle of the sprite, so any purely normal-based term collapses when
 * the sun is in front of or behind the plume. The world-up axis does not collapse, and
 * `rlfSpriteShade` runs its sky-over-ground gradient and its extinction along that axis.
 * Because `rlfDustParams` scales optical thickness with the sprite's own radius, a puff
 * that grows from 0.4 m to 2.5 m does not wash out — its underside gets *darker*, which
 * is what a real expanding dust cloud does.
 *
 * ---------------------------------------------------------------------------
 * THINGS THAT WILL BITE YOU
 *
 * - `rlfBumpH` uses screen-space derivatives, so it is FRAGMENT ONLY. Injecting the
 *   fragment half of the library into a vertex shader will not compile. Use
 *   `reliefGLSL('vertex')` / `reliefGLSL('fragment')` and this cannot happen.
 * - Derivatives are quad-quantised. A field whose period is under ~2 px produces a noisy
 *   normal, which is what makes procedural bump *sparkle*. That is what `rlfReliefAmp`
 *   is for: it fades relief as the square of the Nyquist term while albedo fades
 *   linearly, because a normal aliases long before a colour does.
 * - `rlfFbmD` returns the gradient with respect to its own argument. If you scale the
 *   input you must scale the gradient. `rlfReliefField` does it for you; prefer it.
 * - Sprite `uv` must be the -1..1 quad coordinate. If you modulate the outline (a lumpy
 *   silhouette), divide by the modulation before calling `rlfSpriteNormal`, or the
 *   implied volume will not match the shape you drew.
 * - `patchStandardMaterial` needs a **unique** `cacheKey` per distinct shader source, or
 *   three will hand you another material's compiled program.
 * - `patchStandardMaterial` OVERWRITES `onBeforeCompile`. If you already have one, take
 *   the pieces (path C above) instead.
 *
 * ---------------------------------------------------------------------------
 * VERIFIED
 *
 * Nothing consumes this file yet, so it was verified by compiling and *measuring* it in
 * headless Chromium (the same ANGLE/SwiftShader the capture harness uses), through real
 * three materials, reading float render targets back. What that established:
 *
 * - Five programs compile with zero shader errors and zero GL errors: a patched
 *   MeshStandardMaterial, an instanced+shadowed patched material, two ShaderMaterials,
 *   and three's depth pass alongside them. The stage-agnostic half compiles in a vertex
 *   shader; the fragment half compiles in a fragment shader.
 * - `rlfBumpH` on a known ramp h = 0.1x over flat ground returns exactly
 *   ( -0.099504, 0.995037, 0 ) — the analytic answer to 5 decimal places — and
 *   `rlfBumpSlope` returns the same vector. `amp = 0.5` halves the slope exactly. An
 *   absurd field (h = 500x) is held by the 4:1 cap at y = 0.2425 and never inverts.
 * - `rlfFbmD`'s analytic gradient matches a central difference of its own value with a
 *   cosine of 0.99999 over 65k samples; mean absolute error 3.3e-5 against a mean
 *   gradient of 0.019. The chain rule through the rotated octaves is correct.
 * - `rlfCoverBlend`'s margin is 46 px wide where the level set the critiques complained
 *   about is 2 px — 23x — with the contact minimum (0.819) sitting 26 px OUTSIDE the
 *   edge, and a real 4.5 cm of thickness. With the break enabled the margin crossing has
 *   a standard deviation of ~10 px along its length: fingers, not a contour line.
 * - A dust sprite's top/bottom luminance ratio goes from 1.86 at 0.4 m radius to 3.12 at
 *   2.5 m. The underside gets darker as the puff grows, which is the requirement. With
 *   the sun switched off entirely it still reads 1.62 — it has a top and a bottom in
 *   flat overcast.
 * - `rlfContact` reproduces the disc form factor exactly (0 at the base, 0.5 at y = R,
 *   1 far away); `rlfCrease` is 0.5 at the wall.
 * - `rlfReliefAmp` is monotone: 1.0 near, 0.785 mid, 0.092 far.
 * - A material that forgets `reliefUniforms()` falls back to exactly the documented
 *   midday light rather than to black.
 */

import * as THREE from 'three';
import { clamp01, smoothstep } from '../../core/math';
import { activeSky } from '../sky';
import { registerSystem, type SystemContext } from '../../core/systems';

// ===========================================================================
// Art-direction constants. Single source of truth: the GLSL below is generated
// from these, so TypeScript-side LOD (scatter tile fades, particle sizing) and
// shader-side LOD cannot drift apart.
// ===========================================================================

/**
 * Tunable at runtime. These are *visual* constants and live here rather than in
 * `core/tuning.ts`, per docs/AGENTS.md.
 */
export const ReliefTuning = {
  /** Global multiplier on every relief amplitude. 1 = as authored. */
  masterScale: 1,
  /**
   * How much a fast-moving camera pulls relief back. A procedural bump normal crawls
   * when the camera moves; albedo does not, which is why only relief is cut.
   * Matches the terrain's existing `TerrainShading.motionBumpCut` so adopting this
   * library changes nothing there.
   */
  motionCut: 0.22,
  /** Camera speed, m/s, at which `motionCut` is fully applied. */
  motionFullSpeed: 24,
  /** Sun radiance = light.color * light.intensity * keyScale. 1/pi = Lambertian. */
  keyScale: 1 / Math.PI,
  /** Sky/ground radiance = probe irradiance * probe.intensity * ambientScale. */
  ambientScale: 1 / Math.PI,
};

/**
 * The distance/LOD policy. Every consumer fades on these numbers or the world looks
 * like several different games at different distances.
 *
 * `nyqBias` / `nyqRate` and the anisotropy pair are lifted verbatim from the terrain's
 * existing shader so that terrain adopting this library is a no-op at every distance.
 */
export const ReliefLod = {
  /** Nyquist fade: 1 at the near field, 0 once a feature's period is sub-pixel. */
  nyqBias: 1.45,
  nyqRate: 2.6,
  /** Anisotropy we are willing to pay for before we start trading contrast for it. */
  anisoMax: 4.2,
  /** Contrast lost per unit of anisotropy past `anisoMax`, and its floor. */
  grazeRate: 0.03,
  grazeMin: 0.22,
  /** Art-direction far fade, on top of Nyquist. Metres. */
  farStart: 140,
  farEnd: 470,
  /**
   * What survives at `farEnd`. Not zero: past 470 m the *form* is landform and must
   * still catch light, it is the object-scale detail that has to go. Relief keeps more
   * than albedo detail does, because at that range form is all that is left to read.
   */
  farFloorRelief: 0.55,
  farFloorAlbedo: 0.35,
} as const;

/** GLSL float literal — GLSL has no implicit int->float in most contexts. */
const g = (v: number): string => (Number.isInteger(v) ? v.toFixed(1) : String(v));

// ===========================================================================
// GLSL
// ===========================================================================

/**
 * Varyings the fragment library expects. Declared separately from the library itself so
 * a consumer can take one without the other (a raw ShaderMaterial with its own
 * interpolants may want `reliefGLSL()` and none of these).
 *
 * Include-guarded, so injecting it twice into the same stage is harmless.
 */
export const RELIEF_VARYINGS = /* glsl */ `
#ifndef RLF_VARYINGS
#define RLF_VARYINGS
varying vec3 vRlfWPos;    // world position of this fragment
varying vec3 vRlfWNrm;    // world-space geometric normal (not normalized after interp)
varying float vRlfDist;   // metres from the camera
#endif
`;

/**
 * Vertex body that fills the varyings above. Must run AFTER `#include <project_vertex>`,
 * where `mvPosition` is in scope.
 *
 * The world position is recovered from the *view* position rather than from
 * `modelMatrix * transformed`, using the same trick sky.ts uses for `vGripWP`:
 * `viewMatrix` is orthonormal, so `v * viewMatrix` is its inverse rotation. That is
 * exact for instanced, batched, skinned and morphed geometry alike, which
 * `modelMatrix * transformed` is not — and vegetation, the bike and the scatter are all
 * instanced.
 */
export const RELIEF_VERTEX_BODY = /* glsl */ `
{
  vRlfWPos = cameraPosition + ( vec4( mvPosition.xyz, 0.0 ) * viewMatrix ).xyz;
  vRlfWNrm = ( vec4( transformedNormal, 0.0 ) * viewMatrix ).xyz;
  vRlfDist = length( mvPosition.xyz );
}
`;

/**
 * The library, part 1: everything that is legal in BOTH shader stages.
 *
 * Uses no screen-space derivatives, so it compiles in a vertex shader. The uniform
 * block is declared here; if a consumer forgets to merge `reliefUniforms()` every
 * component reads 0, `rlfEnv()` detects that and falls back to a fixed midday light. It
 * degrades, it does not break.
 */
export const RELIEF_COMMON_GLSL = /* glsl */ `
#ifndef RLF_COMMON
#define RLF_COMMON

// The shared light block. One Float32Array, shared by reference across every material
// that merged reliefUniforms(), so one write per frame updates the whole scene.
//   0: sunDir.xyz (world, toward the sun) | sun visibility 0..1
//   1: key radiance.rgb                   | camera-motion relief cut 0..1
//   2: sky (upper hemisphere) radiance    | time, seconds
//   3: ground-bounce radiance             | master relief scale
uniform vec4 uRlf[ 4 ];

struct RlfEnv {
  vec3 sunW;      // world-space direction TOWARD the sun
  float sunVis;   // 0..1, goes to 0 at and below the horizon
  vec3 key;       // sun radiance, linear
  vec3 sky;       // upper-hemisphere radiance, linear
  vec3 gnd;       // ground-bounce radiance, linear
  float motion;   // 0..1 relief pullback from camera speed
  float time;     // seconds
  float scale;    // global relief master
};

// Sun direction and radiance are what the whole file keys off, so the fallbacks are the
// values the game actually ships at midday. Detection is by the sun vector's length:
// an unwired uniform block is all zeros, and the TS side always writes a unit vector.
RlfEnv rlfEnv() {
  RlfEnv E;
  bool ok = dot( uRlf[ 0 ].xyz, uRlf[ 0 ].xyz ) > 0.25;
  E.sunW   = ok ? normalize( uRlf[ 0 ].xyz ) : vec3( 0.3992, 0.6587, 0.6386 );
  E.sunVis = ok ? uRlf[ 0 ].w : 1.0;
  E.key    = ok ? uRlf[ 1 ].xyz : vec3( 1.00, 0.95, 0.86 );
  E.motion = ok ? uRlf[ 1 ].w : 0.0;
  E.sky    = ok ? uRlf[ 2 ].xyz : vec3( 0.19, 0.23, 0.31 );
  E.time   = ok ? uRlf[ 2 ].w : 0.0;
  E.gnd    = ok ? uRlf[ 3 ].xyz : vec3( 0.12, 0.11, 0.10 );
  E.scale  = ok ? uRlf[ 3 ].w : 1.0;
  return E;
}

// viewMatrix is a rigid transform, so this preserves length: a unit world direction
// comes back as a unit view direction.
vec3 rlfToView( vec3 wDir ) {
  return ( viewMatrix * vec4( wDir, 0.0 ) ).xyz;
}

// ---------------------------------------------------------------------------
// Deterministic hashing. Dave Hoskins style: no sin(), so it is stable across drivers
// and has no period over the world extents.
// ---------------------------------------------------------------------------
float rlfHash21( vec2 p ) {
  vec3 p3 = fract( vec3( p.xyx ) * 0.1031 );
  p3 += dot( p3, p3.yzx + 33.33 );
  return fract( ( p3.x + p3.y ) * p3.z );
}

vec2 rlfHash22( vec2 p ) {
  vec3 p3 = fract( vec3( p.xyx ) * vec3( 0.1031, 0.1030, 0.0973 ) );
  p3 += dot( p3, p3.yzx + 33.33 );
  return fract( ( p3.xx + p3.yz ) * p3.zy );
}

float rlfHash31( vec3 p ) {
  vec3 p3 = fract( p * 0.1031 );
  p3 += dot( p3, p3.zyx + 31.32 );
  return fract( ( p3.x + p3.y ) * p3.z );
}

// Value noise carrying its own analytic derivative: ( value 0..1, dv/dx, dv/dy ).
// Quintic interpolant, so the derivative is continuous and the bump normal does not
// facet along cell boundaries — which is exactly what a cubic interpolant does, and it
// reads as a faint square grid under raking light.
vec3 rlfNoiseD( vec2 p ) {
  vec2 i = floor( p );
  vec2 f = p - i;
  vec2 u = f * f * f * ( f * ( f * 6.0 - 15.0 ) + 10.0 );
  vec2 du = 30.0 * f * f * ( f * ( f - 2.0 ) + 1.0 );
  float a = rlfHash21( i );
  float b = rlfHash21( i + vec2( 1.0, 0.0 ) );
  float c = rlfHash21( i + vec2( 0.0, 1.0 ) );
  float d = rlfHash21( i + vec2( 1.0, 1.0 ) );
  float k1 = b - a;
  float k2 = c - a;
  float k3 = a - b - c + d;
  return vec3(
    a + k1 * u.x + k2 * u.y + k3 * u.x * u.y,
    ( k1 + k3 * u.y ) * du.x,
    ( k2 + k3 * u.x ) * du.y
  );
}

// ~36.7 degrees. Rotating every octave stops them stacking into axis-aligned blotches,
// which is what actually reads as "tiling" in procedural work.
const mat2 RLF_ROT = mat2( 0.80181, 0.59757, -0.59757, 0.80181 );

// fBm carrying its own gradient. Chain rule through the accumulated transform: for
// q = M*p, d/dp = transpose(M) * d/dq, and transpose(M)*v = ( dot(M[0],v), dot(M[1],v) ).
// The lacunarity is deliberately irrational-ish so octaves never re-align.
vec3 rlfFbmD( vec2 p, int oct, float gain ) {
  vec3 acc = vec3( 0.0 );
  float amp = 1.0;
  float norm = 0.0;
  mat2 m = mat2( 1.0, 0.0, 0.0, 1.0 );
  for ( int i = 0; i < 6; i ++ ) {
    if ( i >= oct ) break;
    vec3 n = rlfNoiseD( m * p );
    acc.x += amp * n.x;
    acc.yz += amp * vec2( dot( m[ 0 ], n.yz ), dot( m[ 1 ], n.yz ) );
    norm += amp;
    amp *= gain;
    m = RLF_ROT * m * 2.0173;
  }
  return acc / max( norm, 1e-5 );
}

float rlfFbm( vec2 p, int oct ) {
  return rlfFbmD( p, oct, 0.5 ).x;
}

// A height field in METRES together with its slope in metres per metre, from an fBm at
// 'freq' cycles/m. This exists because forgetting the chain rule on a scaled input is
// the single most common way a procedural bump comes out at the wrong strength: the
// gradient of fbm(uv*freq) with respect to uv is freq times the gradient it returns.
//
// The height is ZERO MEAN and spans +-reliefM/2, so adding two of these does not drift
// the surface upward. Returns ( height m, dh/du, dh/dv ) in the uv's own frame; feed
// .yz straight to rlfBumpSlope and .x to rlfBumpH.
vec3 rlfReliefField( vec2 uv, float freq, float reliefM, int oct ) {
  vec3 n = rlfFbmD( uv * freq, oct, 0.5 );
  return vec3( ( n.x - 0.5 ) * reliefM, n.yz * ( freq * reliefM ) );
}

// ---------------------------------------------------------------------------
// Detail frame. rlfPlaneUV and rlfGroundFrame agree by construction: the UV returned by
// the first is expressed in the axes returned by the second. Use them as a pair, or a
// slope computed in one frame will tip the normal in a different one.
// ---------------------------------------------------------------------------
vec2 rlfPlaneUV( vec3 wp, vec3 N ) {
  vec3 a = abs( N );
  if ( a.y >= max( a.x, a.z ) ) return wp.xz;
  if ( a.x > a.z ) return wp.zy;
  return wp.xy;
}

void rlfGroundFrame( vec3 N, out vec3 axU, out vec3 axV ) {
  vec3 a = abs( N );
  if ( a.y >= max( a.x, a.z ) ) {
    axU = vec3( 1.0, 0.0, 0.0 ); axV = vec3( 0.0, 0.0, 1.0 );
  } else if ( a.x > a.z ) {
    axU = vec3( 0.0, 0.0, 1.0 ); axV = vec3( 0.0, 1.0, 0.0 );
  } else {
    axU = vec3( 1.0, 0.0, 0.0 ); axV = vec3( 0.0, 1.0, 0.0 );
  }
}

// ---------------------------------------------------------------------------
// NORMAL PERTURBATION, analytic branch.
//
// For a height field h displaced along N over a surface with tangents axU/axV, the
// perturbed normal is normalize( N - grad_tangential(h) ). Projecting the component
// along N out of the gradient is what makes this correct on a curved surface rather
// than only on a plane; skipping it is why hand-rolled bump code tends to lose strength
// on steep faces.
// ---------------------------------------------------------------------------
vec3 rlfBumpSlope( vec3 N, vec3 axU, vec3 axV, vec2 slope, float amp ) {
  vec3 gr = axU * slope.x + axV * slope.y;
  gr -= N * dot( gr, N );
  return normalize( N - gr * amp );
}

vec3 rlfBumpXZ( vec3 N, vec2 slopeXZ, float amp ) {
  return rlfBumpSlope( N, vec3( 1.0, 0.0, 0.0 ), vec3( 0.0, 0.0, 1.0 ), slopeXZ, amp );
}

// Cover drawn as its OWN geometry — a ground-plant mat, a moss card, a tread decal —
// must not light itself with its own flat card normal. That is precisely what makes a
// 1-4 m mat hold one value across its whole span while the terrain under it carries the
// light gradient. Blend back toward the ground normal (which the mat's own vertex data
// should carry) and keep only enough of the card's own normal to give it form.
//
// ownWeight 0.25-0.45 is the useful range: below that the mat has no form of its own,
// above it the mat stops belonging to the hillside.
vec3 rlfMatNormal( vec3 groundN, vec3 ownN, float ownWeight ) {
  return normalize( mix( groundN, ownN, clamp( ownWeight, 0.0, 1.0 ) ) );
}

// ---------------------------------------------------------------------------
// CONTACT / EDGE OCCLUSION.
//
// Cheap, analytic, light-independent. Light-independent matters: a contact term that is
// really a shadow disappears in flat overcast, which is exactly when an object floating
// on the ground is most obvious.
// ---------------------------------------------------------------------------

// Occlusion multiplier from a disc-shaped occluder of radius R lying on the ground,
// sampled at radial distance r from its axis and height y above its plane. On axis this
// is the exact form factor of a disc, R^2 / ( R^2 + y^2 ): at y = R it is 0.5, which is
// the number to sanity-check against. Off axis it falls off with the same law, which is
// not exact but is monotonic, smooth, and free.
//
// Returns 1 (open) down to 1 - strength (fully occluded).
float rlfContact( float r, float y, float R, float strength ) {
  float e = max( r - R, 0.0 );
  float rr = R * R;
  return 1.0 - strength * ( rr / ( rr + y * y + e * e + 1e-6 ) );
}

// The same thing addressed by world position: 'centre' is the occluder's base, R its
// radius. Use this for a stone, a tussock, a wheel, a stump — anything sitting on the
// ground that the shadow cascade is too coarse to resolve (a 2048 cascade over 96 m has
// 9 cm texels, so a 25 cm object's whole shadow is three texels and the PCF filter turns
// it into a grey haze).
float rlfContactAt( vec3 wp, vec3 centre, float R, float strength ) {
  vec2 d = wp.xz - centre.xz;
  return rlfContact( length( d ), max( wp.y - centre.y, 0.0 ), R, strength );
}

// Crease darkening beside a step or wall of height 'wallH', at signed horizontal
// distance 'd' (negative = inside the occluded side). This is the analytic ambient
// occlusion of a half-plane: 0.5 * ( 1 - d / sqrt( d^2 + h^2 ) ). Use it for a trail's
// cut bench, the foot of a berm, a rock riser, or the rim of a cover patch.
float rlfCrease( float d, float wallH, float strength ) {
  float dd = max( d, 0.0 );
  return 1.0 - strength * 0.5 * ( 1.0 - dd * inversesqrt( dd * dd + wallH * wallH + 1e-8 ) );
}

// ---------------------------------------------------------------------------
// LOD POLICY.
//
// Stage-agnostic half: everything that does not need derivatives. rlfLodAt() is in the
// fragment half below.
// ---------------------------------------------------------------------------
struct RlfLod {
  float foot;      // metres of world covered by one pixel, anisotropy-budgeted
  float aniso;     // long axis / short axis of the pixel footprint
  float graze;     // 1..RLF_GRAZE_MIN — contrast we can keep at this viewing angle
  float dist;      // metres from the camera
  float farFade;   // 0 near, 1 past RLF_FAR_END
};

const float RLF_NYQ_BIAS = ${g(ReliefLod.nyqBias)};
const float RLF_NYQ_RATE = ${g(ReliefLod.nyqRate)};
const float RLF_ANISO_MAX = ${g(ReliefLod.anisoMax)};
const float RLF_GRAZE_RATE = ${g(ReliefLod.grazeRate)};
const float RLF_GRAZE_MIN = ${g(ReliefLod.grazeMin)};
const float RLF_FAR_START = ${g(ReliefLod.farStart)};
const float RLF_FAR_END = ${g(ReliefLod.farEnd)};
const float RLF_FAR_FLOOR_RELIEF = ${g(ReliefLod.farFloorRelief)};
const float RLF_FAR_FLOOR_ALBEDO = ${g(ReliefLod.farFloorAlbedo)};

float rlfFarFade( float dist ) {
  return smoothstep( RLF_FAR_START, RLF_FAR_END, dist );
}

// For consumers with no world-space derivative to hand: sprites, and anything drawn
// with a known metres-per-pixel. Anisotropy is reported as 1, which is honest for a
// camera-facing quad.
RlfLod rlfLodManual( float dist, float metresPerPixel ) {
  RlfLod L;
  L.foot = max( metresPerPixel, 1e-5 );
  L.aniso = 1.0;
  L.graze = 1.0;
  L.dist = dist;
  L.farFade = rlfFarFade( dist );
  return L;
}

// Nyquist fade for a layer whose spatial frequency is 'freq' cycles/m. 1 = fully
// resolved, 0 = below the sampling limit and must not be drawn at all.
float rlfBandLod( RlfLod L, float freq ) {
  return clamp( RLF_NYQ_BIAS - L.foot * freq * RLF_NYQ_RATE, 0.0, 1.0 );
}

// How much of an albedo pattern at 'freq' to keep here.
float rlfAlbedoAmp( RlfLod L, float freq ) {
  RlfEnv E = rlfEnv();
  return rlfBandLod( L, freq )
    * mix( 1.0, RLF_FAR_FLOOR_ALBEDO, L.farFade )
    * E.scale;
}

// How much RELIEF to keep here. THE multiplier — pass this as 'amp' to every bump call
// and the whole game fades as one thing.
//
// Three deliberate differences from the albedo fade:
//   - it is squared in the Nyquist term, because a strong normal aliases into sparkle
//     long before a colour does;
//   - it is multiplied by 'graze', because at a grazing angle the long axis of the pixel
//     footprint is genuinely undersampled and flat is more honest than brushed fur;
//   - it keeps a higher floor at distance, because past 470 m form is all that is left
//     to read and killing it is what turns a far hillside into a smooth brown dome.
float rlfReliefAmp( RlfLod L, float freq ) {
  RlfEnv E = rlfEnv();
  float n = rlfBandLod( L, freq );
  return n * n * L.graze
    * mix( 1.0, RLF_FAR_FLOOR_RELIEF, L.farFade )
    * ( 1.0 - E.motion )
    * E.scale;
}

// ---------------------------------------------------------------------------
// LIT SPRITES.
//
// The problem with a camera-facing quad is that its own normal points at the camera in
// the middle of the sprite, so every purely normal-based term collapses when the sun is
// in front of or behind it — which is most of the time. What does not collapse is the
// WORLD-UP axis of the sprite, and that is what this shades along: sky above, ground
// bounce below, and an extinction that grows with the sprite's own optical thickness so
// the underside gets darker as the puff expands rather than washing out.
// ---------------------------------------------------------------------------

// Warm key, cool shade. docs/BAR.md: "cool shadows, warm key... saturation comes from
// light, not from albedo". Applied here rather than left to each consumer so every
// sprite in the game agrees.
const vec3 RLF_KEY_WARM = vec3( 1.05, 1.00, 0.92 );
const vec3 RLF_SHADE_COOL = vec3( 0.94, 0.98, 1.08 );

// VERTEX-SIDE helper. World up expressed in the quad's own uv frame, given the same
// spin you used to rotate the quad. Pass the result to rlfSpriteShade as 'upUV'.
//
// If the quad's view offset is rot(spin) * uv, then a view-space direction maps into uv
// space through rot(-spin), which is what this is.
vec2 rlfSpriteUp( float spin ) {
  vec2 u = ( viewMatrix * vec4( 0.0, 1.0, 0.0, 0.0 ) ).xy;
  float l = length( u );
  u = l > 1e-4 ? u / l : vec2( 0.0, 1.0 );
  float c = cos( spin ), s = sin( spin );
  return vec2( u.x * c + u.y * s, - u.x * s + u.y * c );
}

// View-space normal of the volume a sprite implies: a hemisphere, bulged into lobes so
// the key catches one and misses the next. 'd' is the normalized radius (1 at the
// outline); if you modulate the outline, divide uv by the modulation first so the volume
// matches the shape you drew.
vec3 rlfSpriteNormal( vec2 uv, float d, float bulge, float seed ) {
  float z = sqrt( max( 0.0, 1.0 - d * d ) );
  float b = sin( uv.x * 4.6 + seed ) * sin( uv.y * 5.3 - seed * 1.7 );
  return normalize( vec3( uv, z ) + vec3( b * bulge, b * bulge * 0.8, 0.0 ) );
}

struct RlfSpriteParams {
  float wrap;       // 0..0.95, how far the terminator wraps around the volume
  float thickness;  // optical thickness through the sprite's centre, metres
  float ext;        // extinction per metre of thickness
  float trans;      // 0..2 forward-scatter / translucency
  float ao;         // 0..1 self-occlusion of the interior
};

// Dust, smoke, spray. Thickness scales with the sprite's own radius, which is the whole
// answer to "the darker underside must survive the sprite growing": a 0.4 m puff is
// nearly uniform, a 2.5 m puff has a bright crown and a shaded belly, and it gets there
// continuously as it expands. Clamped so a very large puff does not go black.
RlfSpriteParams rlfDustParams( float radiusM ) {
  RlfSpriteParams P;
  P.wrap = 0.55;                                     // dust forward-scatters hard
  P.thickness = clamp( radiusM * 1.3, 0.05, 3.0 );
  P.ext = 0.90;
  P.trans = 0.80;
  P.ao = 0.55;
  return P;
}

// Foliage cards and any thin translucent sheet. Less wrap (a leaf has a real terminator),
// more transmission (chlorophyll passes far more than it reflects).
RlfSpriteParams rlfFoliageParams( float thicknessM ) {
  RlfSpriteParams P;
  P.wrap = 0.25;
  P.thickness = clamp( thicknessM, 0.02, 2.0 );
  P.ext = 0.60;
  P.trans = 1.20;
  P.ao = 0.35;
  return P;
}

// Returns the linear radiance multiplier for the sprite's own albedo. Multiply, do not
// replace: the sprite keeps its authored colour and gets its light from the scene.
//
//   nV     view-space normal from rlfSpriteNormal
//   uv     the -1..1 quad coordinate
//   upUV   world up in the quad's uv frame, from rlfSpriteUp (vertex side)
//   d      normalized radius, 0 at the centre, 1 at the outline
vec3 rlfSpriteShade( vec3 nV, vec2 uv, vec2 upUV, float d, RlfSpriteParams P, RlfEnv E ) {
  vec3 sunV = rlfToView( E.sunW );

  // Where we are between the sprite's underside (0) and its crown (1), anchored to
  // world up. This term does not care how big the sprite is on screen, where the sun is,
  // or which way the camera is facing — which is exactly why it is the one that holds
  // the puff's form together.
  float vert = 0.5 + 0.5 * clamp( dot( uv, upUV ), -1.0, 1.0 );

  // Light entering the crown has to cross the volume to reach the belly. Thickness comes
  // from the sprite's own size, so this deepens as the puff grows.
  float tSun = exp2( - P.thickness * ( 1.0 - vert ) * P.ext );

  // Wrapped Lambert on the implied volume. Wrap is capped well below 1: wrapping at
  // 0.55/0.55 (which is what the old dust shader effectively did) puts the darkest facet
  // of a puff within 20% of the brightest, and a plume whose parts are all the same
  // value has no parts.
  float wr = clamp( P.wrap, 0.0, 0.95 );
  float lam = clamp( ( dot( nV, sunV ) + wr ) / ( 1.0 + wr ), 0.0, 1.0 );

  // Forward scatter. In view space the camera looks down -z, so a sun pointing away from
  // the camera — i.e. behind the sprite — has sunV.z < 0. Weighted toward the rim, which
  // is where a backlit puff actually glows.
  float fwd = max( 0.0, - sunV.z );
  float glow = fwd * fwd * fwd * P.trans * ( 0.35 + 0.65 * d );

  vec3 direct = E.key * E.sunVis * ( lam * tSun + glow ) * RLF_KEY_WARM;

  // Sky over ground bounce. This is the half that survives flat overcast and it is what
  // stops the sprite reading as a cutout when there is no sun at all.
  float vs = vert * vert * ( 3.0 - 2.0 * vert );
  vec3 amb = mix( E.gnd, E.sky, vs ) * RLF_SHADE_COOL;
  // ...and the interior of a thick sprite sees less of it than the rim does.
  amb *= mix( 1.0, exp2( - P.thickness * P.ext * 0.5 * ( 1.0 - d ) ), P.ao );

  return direct + amb;
}

#endif
`;

/**
 * The library, part 2: FRAGMENT ONLY.
 *
 * Everything here uses `dFdx` / `dFdy` / `fwidth`, which do not exist in a vertex
 * shader. Injecting this into a vertex stage is a compile error, which is why
 * `reliefGLSL(stage)` exists — use it and you cannot make the mistake.
 *
 * Must be injected AFTER `RELIEF_COMMON_GLSL`.
 */
export const RELIEF_FRAGMENT_GLSL = /* glsl */ `
#ifndef RLF_FRAGMENT
#define RLF_FRAGMENT

// ---------------------------------------------------------------------------
// LOD, the part that needs to see the pixel footprint.
//
// Taking the major axis of the footprint is what a naive isotropic mip does, and it
// destroys the ground plane you actually ride on: at riding camera angles dFdy sweeps
// many times as much ground as dFdx. So budget some anisotropy — the same trade a
// hardware aniso sampler makes — and pay for the part we cannot afford by fading
// contrast ('graze') rather than by leaving it undersampled.
// ---------------------------------------------------------------------------
RlfLod rlfLodAt( vec3 wp ) {
  RlfLod L;
  vec3 ddx = dFdx( wp );
  vec3 ddy = dFdy( wp );
  float fA = length( ddx ), fB = length( ddy );
  float fLo = max( min( fA, fB ), 1e-5 );
  float fHi = max( fA, fB );
  L.aniso = fHi / fLo;
  L.foot = min( fHi, fLo * RLF_ANISO_MAX ) + 1e-4;
  L.graze = clamp( 1.0 - ( L.aniso - RLF_ANISO_MAX ) * RLF_GRAZE_RATE, RLF_GRAZE_MIN, 1.0 );
  L.dist = length( wp - cameraPosition );
  L.farFade = rlfFarFade( L.dist );
  return L;
}

// ---------------------------------------------------------------------------
// NORMAL PERTURBATION from an arbitrary scalar — the most valuable function in the file.
//
// Mikkelsen's surface-gradient formulation for unparametrized surfaces. It needs no UVs,
// no tangent frame and no knowledge of how 'h' was produced: hand it ANY scalar you
// already computed — a noise field, a cover mask, a distance function, a vertex-colour
// blend — and the thing you were painting becomes form the sun can find.
//
// Derivation, because it is worth being able to check: with sigmaX = dFdx(wp) and
// sigmaY = dFdy(wp) spanning the tangent plane, the tangential gradient of h is
// ( dh/dx * ( sigmaY x N ) + dh/dy * ( N x sigmaX ) ) / det, det = sigmaX . ( sigmaY x N ).
// Multiplying through by |det| avoids the divide and leaves the direction unchanged.
//
// The slope cap matters. 'h' arrives in metres from nine different consumers and the
// first thing anyone gets wrong is its scale; without the cap an over-strength field
// tips the normal past the horizon and the surface lights from inside out, which looks
// like a hole rather than like a mistake. 4:1 is a 76 degree flank — steeper than any
// real ground detail and still recoverable.
//
//   N     world-space normal, normalized
//   wp    world-space position of this fragment
//   h     the scalar field, in METRES of relief above the surface
//   amp   dimensionless strength; 1 = physically true. Pass rlfReliefAmp( L, freq ).
vec3 rlfBumpH( vec3 N, vec3 wp, float h, float amp ) {
  vec3 sx = dFdx( wp );
  vec3 sy = dFdy( wp );
  vec3 r1 = cross( sy, N );
  vec3 r2 = cross( N, sx );
  float det = dot( sx, r1 );
  float ad = abs( det );
  // Degenerate quad (silhouette, or a zero-area triangle): no frame, no bump.
  if ( ad < 1e-12 ) return N;
  vec3 t = ( det < 0.0 ? -amp : amp ) * ( dFdx( h ) * r1 + dFdy( h ) * r2 );
  float cap = ad * 4.0;
  float tl = length( t );
  if ( tl > cap ) t *= cap / tl;
  return normalize( ad * N - t );
}

// Convenience for the common case: a 0..1 field standing 'reliefM' metres proud.
vec3 rlfFieldNormal( vec3 N, vec3 wp, float f01, float reliefM, float amp ) {
  return rlfBumpH( N, wp, f01 * reliefM, amp );
}

// ---------------------------------------------------------------------------
// COVER — the second thing this file exists for.
//
// A cover layer is duff, moss, ground plants, lichen, packed tread: something lying ON
// the ground rather than being the ground. Six critiques in two waves said the same
// thing about ours — that it "reads as flat khaki decals pasted onto the ground", with
// a boundary that is "an fwidth-antialiased level set, so a ~1px hard step with no
// feathering, no contact darkening and no thickness".
//
// All three faults are structural, and all three are fixed here:
//
//   NO LEVEL SET. The feather width is an ART width in field units, floored at 1.5 px
//   rather than being a pixel wide by construction. A margin has to be allowed to be
//   several centimetres of real ground.
//
//   BROKEN MARGIN. The threshold is displaced by an fBm before it is crossed, so the
//   boundary is fingers and tufts rather than a contour line. The break fades at its own
//   Nyquist limit, so at distance the margin simplifies instead of aliasing into a
//   dotted line.
//
//   THICKNESS AND CONTACT. The cover has depth in metres. That depth is a height field,
//   so its rim becomes a lit shoulder through rlfBumpH, and the ground immediately
//   outside the rim gets an occlusion band. Both scale with the depth: a 2 cm duff mat
//   barely registers, a 20 cm moss bolster reads as an object.
//
// And the thing none of that says out loud: the cover NEVER TOUCHES THE LIGHT TERM. It
// blends albedo and roughness and it perturbs the normal, and then three's own lighting
// runs once, from that normal, with the same shadow map, the same analytic ridge shadow
// and the same sky probe the bare ground next to it uses. That is what makes a 4 m mat
// carry the hillside's light gradient across its span instead of sitting at one value.
// ---------------------------------------------------------------------------
struct RlfCoverParams {
  float level;      // 0..1 threshold on 'field' at which cover begins
  float margin;     // feather half-width in 'field' units. THE art width. 0.04-0.10.
  float breakFreq;  // cycles/m of the margin break. 0 disables it (and saves an fBm).
  float breakAmp;   // how far the margin wanders, in 'field' units. 0.15-0.3.
  float depth;      // cover thickness, metres. Drives the rim relief and the contact.
  float occ;        // 0..1 strength of the contact darkening at the rim
  float grain;      // 0..1 how much the cover's own interior varies in thickness
};

// Sane duff / moss / ground-plant values. Start here, change 'depth' and 'level'.
RlfCoverParams rlfCoverDefaults() {
  RlfCoverParams P;
  P.level = 0.50;
  P.margin = 0.055;
  P.breakFreq = 1.6;
  P.breakAmp = 0.20;
  P.depth = 0.045;
  P.occ = 0.45;
  P.grain = 0.55;
  return P;
}

struct RlfCover {
  float w;      // 0..1 blend weight for the cover's albedo and roughness
  float h;      // cover surface height above bare ground, metres — feed this to rlfBumpH
  float occ;    // occlusion multiplier for the LIGHT, 1 = no change
  float edge;   // signed distance to the margin in feather widths, for your own use
};

// A patchy 0..1 field with the right character for cover: patches at 'patchM' metres
// with sub-patches inside them, so the margin is never a single smooth contour and the
// interior is never one value. 'seed' decorrelates two cover layers that share a surface.
//
// You do not have to use this — rlfCoverBlend takes any 0..1 scalar, including a
// vertex-colour mask, a distance-to-trail, or a moisture field. It exists so that nine
// consumers who have no particular opinion produce cover that looks like one game.
float rlfCoverField( vec2 uv, float patchM, float seed ) {
  float f = max( patchM, 0.05 );
  float a = rlfFbm( uv / f + seed, 3 );
  float b = rlfFbm( uv / ( f * 0.31 ) + seed * 1.7 + 13.1, 2 );
  return clamp( a * 0.68 + b * 0.32, 0.0, 1.0 );
}

RlfCover rlfCoverBlend( float field, vec2 uv, RlfLod L, RlfCoverParams P ) {
  RlfCover C;

  // 1. Interrupt the margin. Faded at the break's own Nyquist limit, so a distant patch
  //    keeps its shape and loses its fringe rather than dissolving into speckle.
  float bl = rlfBandLod( L, P.breakFreq );
  float nv = 0.5;
  float brk = 0.0;
  if ( P.breakAmp > 1e-4 && bl > 0.01 ) {
    vec3 n = rlfFbmD( uv * P.breakFreq, 3, 0.55 );
    nv = n.x;
    brk = ( n.x - 0.5 ) * 2.0 * P.breakAmp * bl;
  }
  float fb = field + brk;

  // 2. Feather over an ART width, floored at 1.5 px so it antialiases but is never
  //    *defined* by the pixel size. This one line is the difference between a margin and
  //    a level set.
  float w = max( P.margin, fwidth( fb ) * 1.5 );
  float d = ( fb - P.level ) / max( w, 1e-5 );
  C.edge = d;
  C.w = smoothstep( -1.0, 1.0, d );

  // 3. Thickness, with interior variation so the mat is not a slab. At nv = 0.5 the
  //    variation term is exactly 1, so 'grain' moves the texture without moving the mean.
  C.h = P.depth * C.w * mix( 1.0, 0.4 + 1.2 * nv, P.grain * bl );

  // 4. Contact. A band one feather wide centred just OUTSIDE the margin — the ground
  //    banked against the edge of the mat, not the mat itself. Scaled by depth, because
  //    a thin cover genuinely does not occlude. Because the feather is pixel-floored the
  //    band stays a couple of pixels wide at any range, so the contact line survives to
  //    the horizon instead of being the first thing to disappear.
  float rim = exp2( - abs( d + 0.9 ) * 1.7 );
  C.occ = 1.0 - P.occ * rim * clamp( P.depth * 9.0, 0.0, 1.0 );

  return C;
}

// Apply a cover layer. Blends albedo, blends roughness, perturbs the normal with the
// cover's own thickness. Deliberately does NOT touch 'C.occ': occlusion belongs on the
// light, not on the albedo. In a patched MeshStandardMaterial write it to 'gRlfOcc' and
// the patch puts it on the indirect and direct diffuse where it belongs.
//
//   amp   pass rlfReliefAmp( L, 1.0 / typical patch size ), or just rlfReliefAmp( L, 1.0 )
void rlfCoverApply( inout vec3 albedo, inout float rough, inout vec3 N,
                    vec3 wp, RlfCover C, vec3 coverAlbedo, float coverRough, float amp ) {
  albedo = mix( albedo, coverAlbedo, C.w );
  rough = mix( rough, coverRough, C.w );
  N = rlfBumpH( N, wp, C.h, amp );
}

#endif
`;

/**
 * The library for one shader stage, include-guarded and safe to inject more than once.
 *
 * @param stage `'vertex'` gets the stage-agnostic half only; `'fragment'` gets
 *              everything. Asking for the fragment half in a vertex shader is a compile
 *              error, which is what this function exists to prevent.
 * @param withVaryings include the `vRlfWPos` / `vRlfWNrm` / `vRlfDist` declarations.
 *                     Default true.
 */
export function reliefGLSL(stage: 'vertex' | 'fragment', withVaryings = true): string {
  const v = withVaryings ? RELIEF_VARYINGS : '';
  return stage === 'vertex'
    ? `${v}${RELIEF_COMMON_GLSL}`
    : `${v}${RELIEF_COMMON_GLSL}${RELIEF_FRAGMENT_GLSL}`;
}

// ===========================================================================
// The shared light block
// ===========================================================================

/**
 * Fallback values, byte-identical to the GLSL fallbacks in `rlfEnv()`. The array is
 * seeded with these at construction, so the library produces a correct midday image
 * even if `updateRelief` is never called — a missing frame update degrades the *time of
 * day*, it does not degrade to black.
 */
const RELIEF_DEFAULTS = new Float32Array([
  0.3992, 0.6587, 0.6386, 1.0, // sunW | sunVis
  1.0, 0.95, 0.86, 0.0,        // key  | motion
  0.19, 0.23, 0.31, 0.0,       // sky  | time
  0.12, 0.11, 0.10, 1.0,       // gnd  | master scale
]);

/**
 * Lazily constructed. Every export that touches module state builds it on demand rather
 * than at module scope, so relief.ts is safe inside an import cycle — sky.ts may import
 * this file, and this file imports sky.ts.
 */
let reliefData: Float32Array | null = null;
let reliefShared: Record<string, THREE.IUniform> | null = null;

function reliefBlock(): Float32Array {
  if (reliefData === null) reliefData = RELIEF_DEFAULTS.slice();
  return reliefData;
}

/**
 * The shared uniform block. Merge it into every material that uses the library:
 *
 *   ShaderMaterial:    uniforms: { ...reliefUniforms(), ...mine }
 *   onBeforeCompile:   for ( const k in reliefUniforms() ) shader.uniforms[ k ] = reliefUniforms()[ k ];
 *                      (or just use patchStandardMaterial, which does it for you)
 *
 * The same `IUniform` object — and therefore the same `Float32Array` — is handed to
 * every caller, so one write per frame updates every material in the scene. This is the
 * same mechanism sky.ts uses for `uGripAerial`, and for the same reason: nine pieces
 * cannot each own a copy of the sun.
 *
 * Cost: zero per frame. One `vec4[4]` upload per material per draw.
 */
export function reliefUniforms(): Record<string, THREE.IUniform> {
  if (reliefShared === null) reliefShared = { uRlf: { value: reliefBlock() } };
  return reliefShared;
}

let lastTime = -1;
let motionSmooth = 0;
let havePrevCam = false;
const prevCam = new THREE.Vector3();
const tmpUp = new THREE.Vector3(0, 1, 0);
const tmpDown = new THREE.Vector3(0, -1, 0);
const tmpIrr = new THREE.Vector3();
const tmpCol = new THREE.Color();

/**
 * Pull the shared light block into step with the sky, once per frame.
 *
 * Idempotent within a frame: called twice with the same `time`, the second call
 * recomputes the same values and skips the temporal term entirely. So it is safe for
 * several systems to call it, and it is safe for none of them to (see
 * `RELIEF_DEFAULTS`). `installReliefSystem()` is the tidy way to make sure someone does.
 *
 * Reads `activeSky.current` if a sky exists; leaves the defaults in place if not.
 *
 * Cost: ~40 float ops, two SH evaluations, zero allocation.
 *
 * @param camera the render camera — only used to measure speed for the motion cut
 * @param time   absolute seconds; the same clock `SystemContext.time` carries
 */
export function updateRelief(camera: THREE.Camera, time: number): void {
  const d = reliefBlock();
  const sky = activeSky.current;

  if (sky) {
    const s = sky.sunDir;
    d[0] = s.x; d[1] = s.y; d[2] = s.z;
    // Not a hard horizon: the sun's own disc takes a while to set and a hard cut there
    // pops every sprite in the scene on the same frame.
    d[3] = smoothstep(-0.02, 0.1, s.y);

    tmpCol.copy(sky.sun.color).multiplyScalar(sky.sun.intensity * ReliefTuning.keyScale);
    d[4] = tmpCol.r; d[5] = tmpCol.g; d[6] = tmpCol.b;

    // Sky and ground bounce from the same SH probe three lights everything else with,
    // so a hand-lit ShaderMaterial sits in the same air as a MeshStandardMaterial.
    const ai = sky.probe.intensity * ReliefTuning.ambientScale;
    sky.probe.sh.getIrradianceAt(tmpUp, tmpIrr);
    d[8] = Math.max(tmpIrr.x, 0) * ai;
    d[9] = Math.max(tmpIrr.y, 0) * ai;
    d[10] = Math.max(tmpIrr.z, 0) * ai;
    sky.probe.sh.getIrradianceAt(tmpDown, tmpIrr);
    d[12] = Math.max(tmpIrr.x, 0) * ai;
    d[13] = Math.max(tmpIrr.y, 0) * ai;
    d[14] = Math.max(tmpIrr.z, 0) * ai;
  }

  // Temporal term. Guarded on a strictly positive dt so a second call in the same frame
  // cannot double-step the smoothing.
  const dt = lastTime >= 0 ? time - lastTime : 0;
  if (dt > 0) {
    const speed = havePrevCam ? camera.position.distanceTo(prevCam) / dt : 0;
    prevCam.copy(camera.position);
    havePrevCam = true;
    const target =
      Math.min(speed / ReliefTuning.motionFullSpeed, 1) * ReliefTuning.motionCut;
    // Critically-damped-ish, so one hitchy frame does not pop the relief off the world.
    const k = 1 - Math.exp(-dt * 4);
    motionSmooth += (target - motionSmooth) * (Number.isFinite(k) ? k : 1);
    lastTime = time;
  } else if (lastTime < 0) {
    lastTime = time;
    prevCam.copy(camera.position);
    havePrevCam = true;
  }

  d[7] = motionSmooth;
  d[11] = time;
  d[15] = ReliefTuning.masterScale;

  // A NaN anywhere in this array does two bad things: it poisons every material in the
  // scene at once, and it makes three's uniform flattener take its object path (it tests
  // `firstElem <= 0 || firstElem > 0`, which is false for NaN) and upload garbage.
  // Sixteen comparisons a frame is a rounding error against that.
  for (let i = 0; i < 16; i++) {
    if (!Number.isFinite(d[i])) d[i] = RELIEF_DEFAULTS[i];
  }
}

/**
 * Explicit override, for captures and tests. Pass a world-space direction toward the
 * sun. Overwritten by the next `updateRelief` if a sky exists.
 */
export function setReliefSun(x: number, y: number, z: number): void {
  const d = reliefBlock();
  const l = Math.hypot(x, y, z) || 1;
  d[0] = x / l; d[1] = y / l; d[2] = z / l;
  d[3] = clamp01(smoothstep(-0.02, 0.1, d[1]));
}

let systemInstalled = false;

/**
 * Register a tiny system that calls `updateRelief` once a frame.
 *
 * Idempotent — all nine consumers may call it at module scope and only the first does
 * anything, with no duplicate-id warning in the console. If you are the first piece to
 * land that uses this library, call it; if you are not, calling it anyway is free.
 *
 * Order 5, so the block is current before any consumer's own update runs.
 * Cost: ~40 float ops and two SH evaluations per frame, once for the whole game.
 */
export function installReliefSystem(): void {
  if (systemInstalled) return;
  systemInstalled = true;
  registerSystem({
    id: 'relief',
    order: 5,
    update(_dt: number, ctx: SystemContext) {
      updateRelief(ctx.camera, ctx.time);
    },
  });
}

// ===========================================================================
// MeshStandardMaterial path
// ===========================================================================

/**
 * Declarations the patch injects ahead of your `shade` block. These are the three
 * channels the patch reads back out; write them, and three's lighting does the rest.
 *
 * Exported so a consumer with its own `onBeforeCompile` can wire the same three channels
 * by hand. Add it to the fragment `<common>` block after the library.
 */
export const RELIEF_STD_GLOBALS = /* glsl */ `
// Written by your shade block, consumed by the roughness / normal / AO patches below.
vec3 gRlfNormalW;   // world-space shading normal
float gRlfRough;    // 0..1 roughness
float gRlfOcc;      // 0..1 occlusion multiplier, 1 = no change
`;

/**
 * The occlusion wiring, as a drop-in replacement for `#include <aomap_fragment>`.
 *
 * This is the single most important line in the file for anyone who has an occlusion
 * term and does not know where to put it. Wave 2's post critique measured the ambient AO
 * ring at 0.4% median darkening — "objects get a contact line but sit in uniformly lit
 * space". Multiplying albedo cannot fix that, because the ambient term itself is what is
 * wrong; occlusion has to scale the *irradiance*.
 *
 * Direct light is occluded less than indirect (65%) because a crease still sees the sun
 * until geometry actually blocks it, and taking direct down in full puts a grey halo
 * around everything in bright sunlight.
 */
export const RELIEF_STD_AO_PATCH = /* glsl */ `#include <aomap_fragment>
reflectedLight.indirectDiffuse *= gRlfOcc;
reflectedLight.directDiffuse *= mix( 1.0, gRlfOcc, 0.65 );`;

/**
 * Merge the shared light block into a shader being patched by hand. One line, inside
 * your own `onBeforeCompile`:
 *
 *   material.onBeforeCompile = ( shader ) => { mergeReliefUniforms( shader ); ... };
 *
 * Every caller gets the same `IUniform` objects, so the whole scene shares one sun.
 */
export function mergeReliefUniforms(shader: { uniforms: Record<string, THREE.IUniform> }): void {
  const shared = reliefUniforms();
  for (const k in shared) shader.uniforms[k] = shared[k];
}

export interface ReliefStandardOptions {
  /**
   * Unique per distinct shader source. three caches compiled programs by material type
   * plus this key; two materials sharing a key share a program, which is a silent and
   * extremely confusing bug.
   */
  cacheKey: string;
  /**
   * GLSL that replaces `#include <color_fragment>`. Runs with `vRlfWPos` / `vRlfWNrm` /
   * `vRlfDist`, the whole `rlf*` library, and three's own `diffuseColor` in scope.
   * Write `gRlfNormalW`, `gRlfRough` and `gRlfOcc`; they are pre-initialised to the
   * geometric normal, the material's `roughness` uniform and 1.0, so writing none of
   * them is also valid.
   *
   * Wrap it in braces. You are sharing a scope with three's own chunks.
   */
  shade: string;
  /** Extra GLSL appended to the fragment `<common>` block — your own helpers. */
  fragmentCommon?: string;
  /** Extra GLSL appended to the vertex `<common>` block. */
  vertexCommon?: string;
  /** Extra GLSL appended to the vertex body, after the relief varyings are written. */
  vertexBody?: string;
  /** Uniforms merged in addition to the shared relief block. */
  uniforms?: Record<string, THREE.IUniform>;
  /** Route `gRlfNormalW` into three's shading normal. Default true. */
  normal?: boolean;
  /** Route `gRlfRough` into `roughnessFactor`. Default true. */
  roughness?: boolean;
  /** Route `gRlfOcc` into the diffuse light. Default true. */
  occlusion?: boolean;
}

/**
 * Patch a lit material (MeshStandardMaterial / MeshPhysicalMaterial) to use the relief
 * library, in one call.
 *
 * What it wires:
 *   - the library into the fragment `<common>` block, and its stage-safe half into the
 *     vertex one;
 *   - `vRlfWPos` / `vRlfWNrm` / `vRlfDist`, computed after `<project_vertex>` in a way
 *     that is exact for instanced and skinned geometry;
 *   - the shared light uniforms;
 *   - your `shade` block in place of `<color_fragment>`;
 *   - `gRlfNormalW` -> `normal` (converted to view space, which is where three shades);
 *   - `gRlfRough` -> `roughnessFactor`;
 *   - `gRlfOcc` -> `reflectedLight.indirectDiffuse` in full and `directDiffuse` at 65%.
 *
 * Why `gRlfOcc` goes on the light and not on albedo: the wave-2 post critique measured
 * the ambient AO ring at 0.4% median darkening — "objects get a contact line but sit in
 * uniformly lit space". Multiplying albedo cannot fix that, because albedo is already
 * being multiplied by an ambient term that is itself the problem. Occlusion has to scale
 * the *irradiance*. Direct light is occluded less than indirect because a contact crease
 * still sees the sun until the geometry actually blocks it; 65% is the usual game
 * compromise and it keeps sunlit ground from developing grey haloes.
 *
 * Everything three gives a built-in material survives: shadow receiving, the SH probe,
 * the analytic terrain self-shadow sky.ts injects into `lights_fragment_begin`, the
 * aerial-perspective fog chunks, and ACES.
 *
 * Cost: zero at runtime beyond your own `shade` block. One extra program.
 *
 * NOTE: this OVERWRITES `material.onBeforeCompile` and `material.customProgramCacheKey`.
 * If you already have a patch of your own — terrain-shading does, and it is 300 lines —
 * do not use this function. Use `mergeReliefUniforms`, `RELIEF_STD_GLOBALS` and
 * `RELIEF_STD_AO_PATCH` inside your own `onBeforeCompile` instead; they are the same
 * wiring, exported piecemeal.
 */
export function patchStandardMaterial(
  material: THREE.Material,
  options: ReliefStandardOptions,
): void {
  const extra = options.uniforms;
  const wantNormal = options.normal !== false;
  const wantRough = options.roughness !== false;
  const wantOcc = options.occlusion !== false;

  material.onBeforeCompile = (shader: THREE.WebGLProgramParametersWithUniforms) => {
    mergeReliefUniforms(shader);
    if (extra) for (const k in extra) shader.uniforms[k] = extra[k];

    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `#include <common>\n${reliefGLSL('vertex')}\n${options.vertexCommon ?? ''}`,
      )
      .replace(
        '#include <project_vertex>',
        `#include <project_vertex>\n${RELIEF_VERTEX_BODY}\n${options.vertexBody ?? ''}`,
      );

    let f = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>\n${reliefGLSL('fragment')}\n${RELIEF_STD_GLOBALS}\n${
          options.fragmentCommon ?? ''
        }`,
      )
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
gRlfNormalW = normalize( vRlfWNrm );
gRlfRough = roughness;
gRlfOcc = 1.0;
${options.shade}`,
      );

    // Each of these is skipped rather than silently no-op'd if the material does not
    // have the chunk — a MeshPhongMaterial has no roughnessmap_fragment, and a caller
    // who patches one should find out from the code rather than from the picture.
    if (wantRough && f.indexOf('#include <roughnessmap_fragment>') >= 0) {
      f = f.replace('#include <roughnessmap_fragment>', 'float roughnessFactor = gRlfRough;');
    }
    if (wantNormal && f.indexOf('#include <normal_fragment_maps>') >= 0) {
      // Our normal is world space; three shades in view space.
      f = f.replace(
        '#include <normal_fragment_maps>',
        'normal = normalize( ( viewMatrix * vec4( gRlfNormalW, 0.0 ) ).xyz );',
      );
    }
    if (wantOcc && f.indexOf('#include <aomap_fragment>') >= 0) {
      f = f.replace('#include <aomap_fragment>', RELIEF_STD_AO_PATCH);
    }
    shader.fragmentShader = f;
  };

  material.customProgramCacheKey = () => options.cacheKey;
}

// ===========================================================================
// TypeScript mirror of the LOD policy
//
// CPU-side fades (scatter tile dissolves, particle sizes, imposter switches) must agree
// with the shader or the world will visibly change materials at a distance the shader
// does not. These are the same numbers `ReliefLod` puts in the GLSL.
// ===========================================================================

/** The art-direction far fade: 0 near, 1 past `ReliefLod.farEnd`. */
export function reliefFarFade(dist: number): number {
  return smoothstep(ReliefLod.farStart, ReliefLod.farEnd, dist);
}

/** Nyquist fade for a feature of `freq` cycles/m at a footprint of `foot` metres/pixel. */
export function reliefNyquist(freq: number, foot: number): number {
  return clamp01(ReliefLod.nyqBias - foot * freq * ReliefLod.nyqRate);
}

/**
 * Metres of world covered by one pixel at `dist` metres, for a perspective camera.
 * The shader gets this from `dFdx`; on the CPU it is 2 d tan(fov/2) / pixelHeight.
 */
export function reliefFootprint(
  dist: number,
  fovYDegrees: number,
  pixelHeight: number,
): number {
  const t = Math.tan((fovYDegrees * Math.PI) / 360);
  return (2 * dist * t) / Math.max(pixelHeight, 1);
}

/** The CPU mirror of `rlfReliefAmp`, for anything sized or dissolved on the CPU. */
export function reliefAmp(dist: number, freq: number, foot: number): number {
  const n = reliefNyquist(freq, foot);
  const far = reliefFarFade(dist);
  return (
    n * n * (1 - far * (1 - ReliefLod.farFloorRelief)) * ReliefTuning.masterScale
  );
}

// ===========================================================================
// Reference implementations
//
// These compile and typecheck, which is the point: they are the executable half of the
// documentation, so a change to this file that breaks the contract fails `tsc` here
// rather than nine agents later. Nothing imports them. Copy them, do not call them.
// ===========================================================================

/**
 * WORKED EXAMPLE A — a MeshStandardMaterial ground with duff cover that keeps the
 * hillside's light gradient across its span. See the header doc for the walkthrough.
 */
export function exampleGroundMaterial(): THREE.MeshStandardMaterial {
  const mat = new THREE.MeshStandardMaterial({
    color: new THREE.Color(0.121, 0.104, 0.073),
    roughness: 0.94,
    metalness: 0,
  });

  patchStandardMaterial(mat, {
    cacheKey: 'grip-relief-example-ground-v1',
    shade: /* glsl */ `
{
  vec3 wp = vRlfWPos;
  vec3 N = normalize( vRlfWNrm );
  RlfLod L = rlfLodAt( wp );
  vec2 uv = rlfPlaneUV( wp, N );
  vec3 axU, axV;
  rlfGroundFrame( N, axU, axV );

  // 1. The bare ground gets FORM. 8 cm of relief at 0.7 cycles/m: clod scale. The slope
  //    comes back already in metres per metre, so there is no frequency term to forget.
  vec3 R = rlfReliefField( uv, 0.7, 0.08, 3 );
  N = rlfBumpSlope( N, axU, axV, R.yz, rlfReliefAmp( L, 0.7 ) );

  // 2. Cover. Blends albedo and roughness, adds its own thickness to the normal, hands
  //    back an occlusion multiplier for the rim. It never touches the light term.
  float f = rlfCoverField( uv, 6.0, 11.3 );
  RlfCoverParams P = rlfCoverDefaults();
  P.depth = 0.05;
  RlfCover C = rlfCoverBlend( f, uv, L, P );

  vec3 albedo = diffuseColor.rgb;
  float rough = roughness;
  rlfCoverApply( albedo, rough, N, wp, C, vec3( 0.107, 0.086, 0.055 ), 0.97,
                 rlfReliefAmp( L, 1.0 ) );

  // 3. A stone sitting in the ground, to show the contact term. In real use 'centre'
  //    comes from an instance attribute or a hash-placed position.
  vec3 stone = vec3( floor( wp.x / 7.0 ) * 7.0 + 3.5, wp.y, floor( wp.z / 7.0 ) * 7.0 + 3.5 );
  float contact = rlfContactAt( wp, stone, 0.35, 0.55 );

  gRlfNormalW = N;
  gRlfRough = rough;
  gRlfOcc = C.occ * contact;
  diffuseColor.rgb = albedo;
}`,
  });

  return mat;
}

/**
 * WORKED EXAMPLE B — a raw ShaderMaterial dust sprite with a real lit form.
 *
 * The instance attributes match the ones `src/vfx/particles.ts` already writes
 * (`iPos`, `iCol`, `iShape` = size / spin / softness / seed), so this drops onto the
 * existing particle geometry unchanged.
 */
export function exampleDustSpriteMaterial(): THREE.ShaderMaterial {
  const vert = /* glsl */ `
${reliefGLSL('vertex', false)}

attribute vec3 iPos;
attribute vec4 iCol;
attribute vec4 iShape;   // size, spin, softness, seed

varying vec2 vUv;
varying vec2 vUpUV;
varying vec4 vCol;
varying float vSoft;
varying float vSeed;
varying float vRadius;

void main() {
  // Particles live in world space; expanding in view space is what faces the quad at the
  // camera without a per-particle matrix.
  vec4 mv = modelViewMatrix * vec4( iPos, 1.0 );
  float c = cos( iShape.y ), s = sin( iShape.y );
  vec2 q = vec2( position.x * c - position.y * s, position.x * s + position.y * c );
  mv.xy += q * iShape.x;
  gl_Position = projectionMatrix * mv;

  vUv = position.xy;
  // World up in the quad's own uv frame. This is the axis the shading is anchored to,
  // and it is why the underside stays dark whichever way the camera or the sun points.
  vUpUV = rlfSpriteUp( iShape.y );
  vCol = iCol;
  vSoft = iShape.z;
  vSeed = iShape.w;
  vRadius = iShape.x;
}
`;

  const frag = /* glsl */ `
${reliefGLSL('fragment', false)}

varying vec2 vUv;
varying vec2 vUpUV;
varying vec4 vCol;
varying float vSoft;
varying float vSeed;
varying float vRadius;

void main() {
  float r = length( vUv );
  if ( r > 1.0 ) discard;

  // A perfect disc is the tell that this is a sprite. Three angular octaves off the
  // particle's own seed give every puff an outline nothing else in the plume shares.
  float a = atan( vUv.y, vUv.x );
  float lump = 1.0
    + 0.22 * sin( a * 3.0 + vSeed )
    + 0.14 * sin( a * 5.0 - vSeed * 2.3 )
    + 0.09 * sin( a * 9.0 + vSeed * 3.7 );
  float d = r / max( lump, 1e-3 );
  if ( d > 1.0 ) discard;

  // Divide the uv by the outline modulation so the implied volume matches the shape we
  // actually drew.
  vec3 nV = rlfSpriteNormal( vUv / max( lump, 1e-3 ), d, 0.38, vSeed );
  RlfSpriteParams P = rlfDustParams( vRadius );
  vec3 lit = rlfSpriteShade( nV, vUv, vUpUV, d, P, rlfEnv() );

  // Plateau then falloff, so a puff has an edge and the eye can count the masses in a
  // plume instead of reading their sum as fog.
  float f = smoothstep( 1.0, vSoft, d );

  gl_FragColor = vec4( vCol.rgb * lit, vCol.a * f );

  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

  return new THREE.ShaderMaterial({
    vertexShader: vert,
    fragmentShader: frag,
    uniforms: { ...reliefUniforms() },
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: THREE.NormalBlending,
    side: THREE.DoubleSide,
  });
}
